/**
 * Copyright (c) Simam Digital.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import {
	addDoc,
	collection,
	doc,
	getDocs,
	query,
	serverTimestamp,
	updateDoc,
	where,
} from 'firebase/firestore';

import { getDb } from './firebaseClient';

/**
 * Firestore access for cozy gifting (grow-a-garden social layer).
 *
 * Data model:
 *   gifts/{giftId} -> { fromUid, fromName, toUid, type:'coins', amount,
 *                       claimed:boolean, createdAt, claimedAt }
 *
 * A gift is created by its sender and claimed once by its recipient; see
 * firestore.rules for the ownership + amount constraints that back this.
 */

/**
 * Send a coin gift to another player.
 * @param {{ fromUid:string, fromName:string, toUid:string, amount:number }} gift
 * @returns {Promise<void>}
 */
export async function sendCoinGift({ fromUid, fromName, toUid, amount }) {
	const db = getDb();
	if (!db) return;
	await addDoc(collection(db, 'gifts'), {
		fromUid,
		fromName: fromName || 'A gardener',
		toUid,
		type: 'coins',
		amount,
		claimed: false,
		createdAt: serverTimestamp(),
	});
}

/**
 * Load this player's unclaimed gifts.
 * @param {string} uid
 * @returns {Promise<Array<{ id:string, fromUid:string, fromName:string, amount:number }>>}
 */
export async function loadPendingGifts(uid) {
	const db = getDb();
	if (!db) return [];
	const q = query(
		collection(db, 'gifts'),
		where('toUid', '==', uid),
		where('claimed', '==', false),
	);
	const snap = await getDocs(q);
	return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Mark a gift as claimed. The recipient calls this after applying the reward;
 * the security rules only permit flipping `claimed` to true.
 * @param {string} giftId
 * @returns {Promise<void>}
 */
export async function claimGift(giftId) {
	const db = getDb();
	if (!db) return;
	await updateDoc(doc(db, 'gifts', giftId), {
		claimed: true,
		claimedAt: serverTimestamp(),
	});
}
